import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Filter, FilterOperator, SortOrder, Comparator } from '@/lib/utils';
import { axiosClientAdmin } from '@/lib/auth';
import {
  AdminUsersSearched,
  AdminUserForm,
  AdminUserCrudded,
  UserInfoDto,
  User,
} from '../userTypes';
import { useAuthStore } from '@/store/authStore';

interface UseUsersParams {
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: SortOrder;
  searchValue?: string;
  enabled?: boolean;
}

export function useUsers({
  pageNumber = 1,
  pageSize = 100,
  sortBy = 'firstName',
  sortDir = SortOrder.Ascending,
  searchValue = '',
  enabled = true,
}: UseUsersParams = {}) {
  const searchFilters: Filter[] = searchValue
    ? [
        { field: 'email', value: searchValue, action: Comparator.ILike },
        { field: 'firstName', value: searchValue, action: Comparator.ILike },
        { field: 'lastName', value: searchValue, action: Comparator.ILike },
        { field: 'phoneNumber', value: searchValue, action: Comparator.ILike },
      ]
    : [];

  const filterFilterGroups = [
    ...(searchFilters.length
      ? [
          {
            operator: FilterOperator.Or,
            filters: searchFilters,
          },
        ]
      : []),
  ];

  return useQuery<AdminUsersSearched>({
    queryKey: ['users', pageNumber, pageSize, sortBy, sortDir, searchValue],
    queryFn: async () => {
      const { data } = await axiosClientAdmin.get('/Users', {
        params: {
          PageNumber: pageNumber,
          PageSize: pageSize,
          SortBy: sortBy,
          SortDir: sortDir,
          'Filter.Operator': FilterOperator.And,
          'Filter.Filters': [],
          'Filter.FilterGroups': filterFilterGroups,
        },
      });
      return {
        ...data,
        rows: data.rows,
        totalRows: data.totalRows,
      };
    },
    enabled,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AdminUserForm) => {
      const response = await axiosClientAdmin.post('/Users', data);
      return response.data as AdminUserCrudded;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UserInfoDto }) => {
      const response = await axiosClientAdmin.post(`/Users/${id}`, { ...data, userId: id });
      return response.data as User;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  const auth = useAuthStore();

  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await axiosClientAdmin.delete(
        `Tenant/${auth.tenant?.tenantId}/Users/${userId}`
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useUser(userId: string) {
  return useQuery<User>({
    queryKey: ['users', userId],
    queryFn: async () => {
      const { data } = await axiosClientAdmin.get('/Users', {
        params: {
          PageNumber: 1,
          PageSize: 1,
          'Filter.Operator': FilterOperator.And,
          'Filter.Filters': [
            {
              field: 'id',
              value: userId,
              action: Comparator.Eq,
            },
          ],
          'Filter.FilterGroups': [],
        },
      });
      return data.rows[0];
    },
    enabled: !!userId,
  });
}

export function useUpdateUserRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      userId, 
      role, 
      enabled 
    }: { 
      userId: string; 
      role: string; 
      enabled: boolean 
    }) => {
      const { data } = await axiosClientAdmin.post(
        `Users/permission/${userId}/${role}/${enabled}`
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export interface ProjectSearchUserDto {
  id?: string;
  projectId?: string;
  projectName?: string | null;
  customerId?: string;
  customerName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  alertNotificationsEnabled?: boolean;
  externalId?: string | null;
  memberType?: string;
}

export interface ProjectUsersSearched {
  success?: boolean;
  message?: string | null;
  totalRows?: number;
  rows?: ProjectSearchUserDto[] | null;
}

export function useUserProjects(userId: string) {
  return useQuery<ProjectUsersSearched>({
    queryKey: ['userProjects', userId],
    queryFn: async () => {
      const { data } = await axiosClientAdmin.post(`/Users/${userId}/project/search`, null, {
        params: {
          PageNumber: 1,
          // Fetches up to 100 assignments. Users with more will need pagination support.
          PageSize: 100,
        },
      });
      return data;
    },
    enabled: !!userId,
  });
}
